import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Instagram, Mail, MapPin, Phone, Send, Twitter, Youtube } from "lucide-react";
import Logo from "@/components/Logo";
import { ADDRESS, API, EMAIL, NAV_LINKS, PHONE, PHONE_LINK } from "@/data/site";

const SERVICE_LINKS = [
  { to: "/tours", label: "India Tour Packages" },
  { to: "/cab-taxi", label: "Cab & Taxi Booking" },
  { to: "/international", label: "International Holidays" },
  { to: "/contact", label: "Custom Honeymoons" },
  { to: "/contact", label: "Corporate Travel" },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", testid: "social-instagram" },
  { icon: Facebook, label: "Facebook", testid: "social-facebook" },
  { icon: Twitter, label: "Twitter", testid: "social-twitter" },
  { icon: Youtube, label: "YouTube", testid: "social-youtube" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list — journey ideas coming soon.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#090D12]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="text-sm text-[#F5F5F0]/60 leading-relaxed mt-5 max-w-xs">
            Journeys designed around you — across India's mountains and ghats, and the world's
            most beautiful horizons. Always on the road, always reachable.
          </p>
          <div className="flex gap-3 mt-6">
            {SOCIALS.map(({ icon: Icon, label, testid }) => (
              <a
                key={label}
                href="#"
                aria-label={label}
                data-testid={testid}
                className="w-9 h-9 rounded-full glass flex items-center justify-center text-[#F5F5F0]/70 hover:text-[#F2C230] hover:border-[#F2C230]/40 transition-colors duration-300"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Quick links">
          <h4 className="font-sora text-sm font-semibold tracking-[0.2em] uppercase text-[#F5F5F0]/90">Explore</h4>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-[#F5F5F0]/60 hover:text-[#3FB6D3] transition-colors duration-300">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Services">
          <h4 className="font-sora text-sm font-semibold tracking-[0.2em] uppercase text-[#F5F5F0]/90">Services</h4>
          <ul className="mt-5 space-y-3">
            {SERVICE_LINKS.map((s) => (
              <li key={s.label}>
                <Link to={s.to} className="text-sm text-[#F5F5F0]/60 hover:text-[#3FB6D3] transition-colors duration-300">
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h4 className="font-sora text-sm font-semibold tracking-[0.2em] uppercase text-[#F5F5F0]/90">Stay Inspired</h4>
          <form onSubmit={subscribe} className="mt-5 flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-email-input"
              className="input-dark !rounded-full flex-1"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-submit-button"
              aria-label="Subscribe"
              className="btn-gold !px-4 !py-3 shrink-0"
            >
              <Send size={16} />
            </button>
          </form>
          <ul className="mt-6 space-y-3 text-sm text-[#F5F5F0]/60">
            <li className="flex items-start gap-3">
              <Phone size={15} className="text-[#F2C230] mt-0.5 shrink-0" />
              <a href={PHONE_LINK} className="hover:text-[#F2C230] transition-colors">{PHONE}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={15} className="text-[#F2C230] mt-0.5 shrink-0" />
              <a href={`mailto:${EMAIL}`} className="hover:text-[#F2C230] transition-colors">{EMAIL}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-[#F2C230] mt-0.5 shrink-0" />
              <span>{ADDRESS}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#F5F5F0]/45">
            © {new Date().getFullYear()} Orego Tours. All rights reserved.{" "}
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[#3FB6D3] transition-colors">
              Admin
            </Link>
          </p>
          <p className="text-xs text-[#F5F5F0]/45">Crafted for the road. Available 24×7.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
